import { ColumnDef } from '@tanstack/react-table';
import {
  Button,
  Flex,
  Icon,
  Text,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  RadioGroup,
  Stack,
  Radio,
  Input,
  TextLabel,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon, SyncIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SERVICE_ID_NAME_MAP } from '../../../../utils/service';
import {
  PolicyItemInTable,
  PolicyListModalName,
  PolicyListModalState,
  OverAllPolicyType,
  ScanType,
  ScanScopeType,
} from '../../utils/types';
import { PolicyRulesCell } from '../../TableCell';
import CommonTable from '../../../../components/Common/Table';
import {
  ManualScanCancelAndRescanInfo,
  ScanTypeCheckBoxGroup,
} from '@Policy/OverAllPolicyList/Modal/scan';
import TimeRange from '@Policy/TimeRange';
import dayjs from 'dayjs';
import { PolicyApi } from '@Apis';
import utc from 'dayjs/plugin/utc';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { OverAllPolicyTypeContext } from '@Policy/OverAllPolicyList/context/OverAllPolicyTypeContext';
import isNumber from 'lodash/isNumber';
import LoadingBtn from '@Components/Common/LoadingBtn';

dayjs.extend(utc);

interface Props {
  policyId4ManualScan: string;
  setPolicyId4ManualScan: (policyId: string) => void;
  modal: PolicyListModalState;
  setModal: ({ name, policy }: PolicyListModalState) => void;
  setRefreshTableTrigger: (trigger: Date) => void;
}

export const ScanningModal = ({
  policyId4ManualScan,
  setPolicyId4ManualScan,
  modal,
  setModal,
  setRefreshTableTrigger,
}: Props) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  // const timeModel = useUtcOffsetStore();
  const [loading, toggleLoading] = useToggle(false);
  const { t } = useTranslation();
  const [tableData, setTableData] = useState<PolicyItemInTable>({
    ...modal.policy,
  } as PolicyItemInTable);
  const overAllPolicyType = useContext(OverAllPolicyTypeContext);

  const taskParam = useMemo(() => {
    try {
      const parsedTaskParam = JSON.parse(modal.policy?.taskParam as string);
      return parsedTaskParam;
    } catch (e) {
      console.error('taskParam is not a valid json string');
      return {};
    }
  }, [modal.policy?.taskParam]);

  const formatTime = useCallback((totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds - hours * 3600) / 60);
    const seconds = totalSeconds - hours * 3600 - minutes * 60;

    const hoursFormat = hours < 10 ? '0' + hours : hours.toString();
    const minutesFormat = minutes < 10 ? '0' + minutes : minutes.toString();
    const secondsFormat = seconds < 10 ? '0' + seconds : seconds.toString();

    return hoursFormat + ':' + minutesFormat + ':' + secondsFormat;
  }, []);

  const calElapsedTime = useCallback(() => {
    const startTime = dayjs.utc(modal.policy?.minStart);
    const nowTime = dayjs.utc();
    const diffInSeconds = nowTime.diff(startTime, 'second');
    return formatTime(diffInSeconds);
  }, [formatTime, modal.policy?.minStart]);

  const onClose = useCallback(() => {
    setPolicyId4ManualScan('');
    setModal({
      name: PolicyListModalName.None,
      policy: null,
    });
  }, [setModal, setPolicyId4ManualScan]);

  const onCancelScan = useCallback(async () => {
    if (!modal.policy) return;
    toggleLoading(true);
    const cancelManualScanInfo: ManualScanCancelAndRescanInfo = {
      policyType: modal.policy.policyType,
    };
    await PolicyApi.cancelManualScan(
      modal.policy?.scanHistory[0]?.['taskId'],
      cancelManualScanInfo
    );
    toggleLoading(false);
    onClose();
    setRefreshTableTrigger(new Date());
  }, [modal.policy, onClose, setRefreshTableTrigger, toggleLoading]);

  //reform data
  useEffect(() => {
    if (!modal.policy) return;
    try {
      setTableData({
        ...modal.policy,
        policyName: modal.policy?.policyName,
        policyTarget: modal.policy?.policyTarget,
        policyType: modal.policy?.policyType,
        isAsEnabled: modal.policy?.isAsEnabled,
        isVsEnabled: modal.policy?.isVsEnabled,
        isFbEnabled: modal.policy?.isFbEnabled,
        isWtpEnabled: modal.policy?.isWtpEnabled,
        // manual scan 不涉及 va
        isDdaEnabled: false,
        isRaiEnabled: false,
        scanDetails:
          modal.policy?.policyUuid === policyId4ManualScan
            ? 'Done'
            : calElapsedTime(),
      });
    } catch (e) {
      throw new Error('taskParam is not a valid json string');
    }
  }, [calElapsedTime, modal.policy, policyId4ManualScan]);

  const columns: ColumnDef<PolicyItemInTable>[] = useMemo(
    () => [
      {
        id: 'policyName',
        header: t('policy.table.fields.policyName'),
        accessorKey: 'policyName',
        size: 143,
      },
      {
        id: 'policyType',
        header: t('policy.table.fields.policyType'),
        accessorFn: ({ policyType }) =>
          t('common.service.' + SERVICE_ID_NAME_MAP[policyType]) as string,
        size: 134,
      },
      {
        id: 'policyTarget',
        header: t('policy.table.fields.policyTarget'),
        accessorKey: 'policyTarget',
        size: 128,
      },
      {
        id: 'policyRule',
        header: t('policy.table.fields.policyRule'),
        cell: ({ row }) => {
          return (
            <PolicyRulesCell type={overAllPolicyType} policy={row.original} />
          );
        },
        size: 285,
        meta: {
          needOverflowTooltip: false,
        },
      },
      {
        id: 'scanDetails',
        header: t('policy.table.fields.scanDetails'),
        accessorKey: 'scanDetails',
        cell: ({ row }: any) => (
          <Flex alignItems="flex-start" direction="column">
            {row.original.scanDetails === 'Done' ? (
              <Flex>{t('policy.scanningTable.done')}</Flex>
            ) : (
              <Flex alignItems="flex-start" direction="column">
                <Flex alignItems="flex-start">
                  <Icon as={SyncIcon} />
                  <Text ml="4px">{t('policy.status.scanning')}</Text>
                </Flex>
                <Flex alignItems="flex-start">
                  {t('policy.scanningTable.time')} {row.original.scanDetails}
                </Flex>
              </Flex>
            )}
          </Flex>
        ),
        size: 280,
      },
    ],
    [overAllPolicyType, t]
  );

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={modal.name === PolicyListModalName.Scanning}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>
          {overAllPolicyType === OverAllPolicyType.ATP
            ? t('policy.modals.manualScan.atp.title')
            : t('policy.modals.manualScan.dlp.title')}
        </ModalHeader>
        <ModalBody overflow="visible">
          <Flex flexDirection="column">
            <Text paddingY="12px">
              {t('policy.modals.manualScan.selectedPolicy.title')}
            </Text>
            <CommonTable<PolicyItemInTable>
              data={[tableData]}
              columns={columns}
              identityKeys={['scanDetails']}
            />
            {modal.policy?.policyType !== PolicyTypeEnum.Box &&
              modal.policy?.policyType !== PolicyTypeEnum.Dropbox &&
              modal.policy?.policyType !== PolicyTypeEnum.GoogleDrive && (
                <Flex flexDirection="column">
                  <Space height="10px"></Space>
                  <Text>{t('policy.modals.manualScan.scanType.title')}</Text>
                  <ScanTypeCheckBoxGroup
                    scanType={
                      // gmail scan_mode 0 | 1
                      // o365 scan_mode '0' | '1'
                      Number(taskParam['scan_mode']) === 1
                        ? ScanType.ScanOnly
                        : ScanType.ScanAndProtect
                    }
                    withEditPermission={false}
                  />
                </Flex>
              )}
            <Text>{t('policy.modals.manualScan.scanScope.title')}</Text>
            <Space height="10px" />
            {modal.policy?.policyType !== PolicyTypeEnum.Box &&
              modal.policy?.policyType !== PolicyTypeEnum.Dropbox &&
              modal.policy?.policyType !== PolicyTypeEnum.GoogleDrive && (
                <RadioGroup
                  value={
                    taskParam['scan_range'] === '0'
                      ? ScanScopeType.FixedDay
                      : ScanScopeType.Custom
                  }
                  // onChange={(value: ScanScopeType) => setScopeType(value)}
                  disabled={true}
                >
                  <Stack spacing="1x" shouldWrapChildren>
                    <Radio value={ScanScopeType.FixedDay}>
                      <Flex align="center">
                        <Text flex="max-content">
                          {t('policy.modals.manualScan.scanScope.recently')}:
                        </Text>
                        <Input
                          type="number"
                          min={1}
                          max={31}
                          mx="2x"
                          value={
                            isNumber(taskParam['value']?.[0])
                              ? taskParam['value']?.[0]
                              : 1
                          }
                          width="80px"
                          disabled={true}
                        />
                        <Text>
                          {t('policy.modals.manualScan.scanScope.days')}
                        </Text>
                      </Flex>
                    </Radio>
                    <Radio value={ScanScopeType.Custom} mt="8px">
                      <Flex align="center">
                        <Text>
                          {t('policy.modals.manualScan.scanScope.from')}:
                        </Text>
                        <Space width="27px" />
                        <TimeRange
                          range={{
                            startDate:
                              taskParam['value']?.[0]?.length > 3
                                ? dayjs(taskParam['value']?.[0]).toDate()
                                : dayjs().subtract(10, 'days').toDate(),
                            startTime:
                              taskParam['value']?.[0]?.length > 3
                                ? dayjs(taskParam['value']?.[0]).format(
                                    'HH:mm:ss'
                                  )
                                : '00:00:00',
                            endDate:
                              taskParam['value']?.[1]?.length > 3
                                ? dayjs(taskParam['value']?.[1]).toDate()
                                : dayjs().toDate(),
                            endTime:
                              taskParam['value']?.[1]?.length > 3
                                ? dayjs(taskParam['value']?.[1]).format(
                                    'HH:mm:ss'
                                  )
                                : dayjs().format('HH:mm:ss'),
                          }}
                          setRange={() => {
                            return;
                          }}
                          withEditPermission={false}
                        />
                      </Flex>
                    </Radio>
                  </Stack>
                </RadioGroup>
              )}
            {(modal.policy?.policyType === PolicyTypeEnum.Box ||
              modal.policy?.policyType === PolicyTypeEnum.Dropbox ||
              modal.policy?.policyType === PolicyTypeEnum.GoogleDrive) && (
              <RadioGroup
                value={
                  taskParam['type'] === 'offset'
                    ? ScanScopeType.FixedDay
                    : ScanScopeType.Custom
                }
                disabled={true}
              >
                <Stack spacing="1x" shouldWrapChildren>
                  <Radio value={ScanScopeType.FixedDay}>
                    <Flex align="center">
                      <Text flex="max-content">
                        {t('policy.modals.manualScan.scanScope.recently')}:
                      </Text>
                      <Input
                        type="number"
                        mx="2x"
                        value={
                          isNumber(taskParam['value1']?.[0])
                            ? taskParam['value1']?.[0]
                            : 1
                        }
                        width="80px"
                        disabled={true}
                      />
                      <Text>
                        {t('policy.modals.manualScan.scanScope.days')}
                      </Text>
                    </Flex>
                  </Radio>
                  <Radio value={ScanScopeType.Custom} mt="8px">
                    <Flex align="center">
                      <Text>
                        {t('policy.modals.manualScan.scanScope.from')}:
                      </Text>
                      <Space width="27px" />
                      <TimeRange
                        range={{
                          startDate:
                            dayjs(taskParam['value1']).toDate() ||
                            dayjs().subtract(10, 'days').toDate(),
                          startTime:
                            dayjs(taskParam['value1']).format('HH:mm:ss') ||
                            '00:00:00',
                          endDate:
                            dayjs(taskParam['value2']).toDate() ||
                            dayjs().toDate(),
                          endTime:
                            dayjs(taskParam['value2']).format('HH:mm:ss') ||
                            dayjs().format('HH:mm:ss'),
                        }}
                        setRange={() => {
                          return;
                        }}
                        withEditPermission={false}
                      />
                    </Flex>
                  </Radio>
                </Stack>
              </RadioGroup>
            )}
            <Space height="10px" />
            {modal.policy?.policyType !== PolicyTypeEnum.Box &&
              modal.policy?.policyType !== PolicyTypeEnum.Dropbox &&
              modal.policy?.policyType !== PolicyTypeEnum.GoogleDrive && (
                <Space height="10px" />
              ) && (
                <Flex flexDirection="column">
                  <Text>{t('policy.modals.manualScan.recipients.title')}</Text>
                  <Space height="12px" />
                  <Input
                    disabled={true}
                    placeholder={t('policy.placeholder.inputEmailAddresses')}
                    value={taskParam['email_address']}
                  />
                </Flex>
              )}
            <Space height="10px" />
            <Flex align="center">
              <Icon as={InfoOIcon} color={colorStyle.color.info} />
              <Space width="8px" />
              <TextLabel>{t('policy.tips.hints.scaNotIncludeVA')}</TextLabel>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn
            loading={loading}
            onClick={onCancelScan}
            variant="primary"
            disabled={modal.policy?.policyUuid === policyId4ManualScan}
          >
            {t('policy.button.stopScan')}
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
